import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AlertTriangle, BrainCircuit, Loader2, RefreshCw } from 'lucide-react';
import { trainingApi } from '../services/api';
import TrainingTabs, { BOARDS } from '../components/training/TrainingTabs';
import { BOARD_VIEW } from '../components/training/Boards';

/** One Model Training dashboard for the selected programme (EP-26 FE-4). */
const STATE_NOTE: Record<string, string> = {
  not_provisioned: 'Not provisioned', unreachable: 'Store unreachable', forbidden: 'No access to the store',
  no_credentials: 'No read credential on this server',
};

export default function TrainingDashboardPage() {
  const { programId = '', board = '' } = useParams<{ programId: string; board: string }>();
  const [data, setData] = useState<Record<string, any> | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [stream, setStream] = useState('');
  const streamed = ['runs', 'models', 'funnel', 'gates'].includes(board);

  const load = useCallback(() => {
    setLoading(true);
    trainingApi.dashboard(programId, board, streamed ? stream : '')
      .then((d) => { setData(d); setError(''); })
      .catch((e) => setError(String(e?.message || e)))
      .finally(() => setLoading(false));
  }, [programId, board, stream, streamed]);

  useEffect(() => { setData(null); load(); }, [load]);

  const View = BOARD_VIEW[board];
  const label = BOARDS.find((b) => b.path === board)?.label || board;
  return (
    <div className="p-6 space-y-4">
      <header className="flex items-center gap-3">
        <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2"><BrainCircuit className="w-5 h-5" /> Model Training</h1>
        {streamed && (
          <select value={stream} onChange={(e) => setStream(e.target.value)} title="Filter by stream (FE-13): records from before the streams have none"
            className="ml-auto px-2 py-1 text-xs border rounded dark:bg-gray-700 dark:border-gray-600">
            <option value="">All streams</option>
            {Array.from(new Set(['atom', 'compound', ...((data?.streams as string[]) || [])])).map((x) => <option key={x} value={x}>{x} stream</option>)}
          </select>)}
        <button type="button" onClick={load} className={`${streamed ? '' : 'ml-auto '}text-xs flex items-center gap-1 text-gray-600 dark:text-gray-300 hover:text-accent`}>
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </header>
      <TrainingTabs programId={programId} />
      <h2 className="sr-only">{label}</h2>
      {error && <p className="text-sm text-red-600">{error}</p>}
      {!data && !error && <p className="text-sm text-gray-500 flex items-center gap-1"><Loader2 className="w-4 h-4 animate-spin" /> Reading {label.toLowerCase()}…</p>}
      {data && data.state !== 'ok' && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 p-3 text-sm flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
          <span><strong>{STATE_NOTE[data.state] || data.state}.</strong> {data.message}</span>
        </div>
      )}
      {data && data.state === 'ok' && View ? <View d={data} /> : null}
      {!View && <p className="text-sm text-gray-600">No dashboard named {board}.</p>}
    </div>
  );
}
